import { useState } from 'react';
import useSettings from '../hooks/useSettings';
import MobileOptimizedInput from '../components/MobileOptimizedInput';
import MobileOptimizedTextarea from '../components/MobileOptimizedTextarea';
import MobileOptimizedButton from '../components/MobileOptimizedButton';
import MobileOptimizedForm from '../components/MobileOptimizedForm';
import useWindowSize from '../hooks/useWindowSize';
import '../styles/contact.css';

const Contact = () => {
  //const { getContactInfo, getActiveSocialMedia, getMapSettings } = useSettings();
  const { getContactInfo, getActiveSocialMedia } = useSettings();
  const { isMobile: _isMobile } = useWindowSize();

  // Obtener información de configuración
  const contactInfo = getContactInfo();
  const socialMedia = getActiveSocialMedia();
  //const mapSettings = getMapSettings();

  // Estado para el formulario
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  // Estado para los mensajes de éxito o error
  const [formStatus, setFormStatus] = useState({
    message: '',
    isError: false,
    isSubmitted: false,
    isSubmitting: false,
  });

  // Manejar cambios en los campos del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Manejar el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validación básica
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setFormStatus({
        message: 'Por favor, completa todos los campos.',
        isError: true,
        isSubmitted: true,
        isSubmitting: false,
      });
      return;
    }

    // Validaciones adicionales
    if (formData.name.length > 100) {
      setFormStatus({
        message: 'El nombre no puede tener más de 100 caracteres.',
        isError: true,
        isSubmitted: true,
        isSubmitting: false,
      });
      return;
    }

    if (formData.subject.length > 200) {
      setFormStatus({
        message: 'El asunto no puede tener más de 200 caracteres.',
        isError: true,
        isSubmitted: true,
        isSubmitting: false,
      });
      return;
    }

    if (formData.message.length > 2000) {
      setFormStatus({
        message: 'El mensaje no puede tener más de 2000 caracteres.',
        isError: true,
        isSubmitted: true,
        isSubmitting: false,
      });
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus({
        message: 'Por favor, introduce un email válido.',
        isError: true,
        isSubmitted: true,
        isSubmitting: false,
      });
      return;
    }

    // Iniciar envío
    setFormStatus({
      message: 'Enviando mensaje...',
      isError: false,
      isSubmitted: false,
      isSubmitting: true,
    });

    try {
      // Enviar datos a la API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Éxito
        setFormStatus({
          message: data.message || '¡Gracias por tu mensaje! Te responderemos lo antes posible.',
          isError: false,
          isSubmitted: true,
          isSubmitting: false,
        });

        // Resetear el formulario
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
        });

        // Ocultar el mensaje después de 8 segundos
        setTimeout(() => {
          setFormStatus({
            message: '',
            isError: false,
            isSubmitted: false,
            isSubmitting: false,
          });
        }, 8000);

      } else {
        // Error del servidor
        throw new Error(data.message || 'Error al enviar el mensaje');
      }

    } catch (error) {
      console.error('Error al enviar mensaje de contacto:', error);

      setFormStatus({
        message: error.message || 'Error al enviar el mensaje. Por favor, inténtalo de nuevo.',
        isError: true,
        isSubmitted: true,
        isSubmitting: false,
      });

      // Ocultar el mensaje de error después de 8 segundos
      setTimeout(() => {
        setFormStatus({
          message: '',
          isError: false,
          isSubmitted: false,
          isSubmitting: false,
        });
      }, 8000);
    }
  };

  // Función helper para generar URL de iframe de Google Maps
  //const generateMapIframeUrl = (lat, lng, zoom = 15) => {
    // Usar Google Maps con el método más simple y efectivo
   // const zoomValue = Math.max(10, Math.min(20, zoom)); // Limitar zoom entre 10 y 20 para mejor visualización

    // Usar la URL de Google Maps que funciona de forma consistente
   // const query = `${lat},${lng}`;
   // return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&t=&z=${zoomValue}&ie=UTF8&iwloc=&output=embed`;
  //};

  // Estilos inline para asegurar que las secciones ocupen todo el ancho disponible
  const sectionStyle = {
    width: '100%',
    margin: 0,
    padding: '60px 0',
    boxSizing: 'border-box',
  };

  const containerStyle = {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    boxSizing: 'border-box',
  };

  return (
    <>
      {/* Page Header Section */}
      <section
        className="page-header"
        style={{
          ...sectionStyle,
          backgroundColor: 'var(--primary-light)',
          textAlign: 'center',
          padding: '40px 0',
        }}
      >
        <div className="container" style={containerStyle}>
          <h1 style={{ fontSize: '2.5rem', color: 'var(--primary-dark)', marginBottom: '10px' }}>
            Contacto
          </h1>
          <p style={{ fontSize: '1.2rem', color: 'var(--text-primary)' }}>
            Estamos aquí para ayudarte. Ponte en contacto con nosotros.
          </p>
        </div>
      </section>

      {/* Contact Content Section */}
      <section
        className="contact-section"
        style={{ ...sectionStyle, backgroundColor: 'var(--background-light)' }}
      >
        <div className="container" style={containerStyle}>
          <div
            className="contact-content"
            style={{ display: 'flex', gap: '40px', marginBottom: '60px', flexWrap: 'wrap' }}
          >
            <div className="contact-info" style={{ flex: '1', minWidth: '300px' }}>
              <h2 style={{ fontSize: '2rem', color: 'var(--primary-dark)', marginBottom: '20px' }}>
                Información de Contacto
              </h2>
              <p style={{ marginBottom: '30px', fontSize: '1.1rem', lineHeight: '1.6' }}>
                Si tienes alguna pregunta o comentario, no dudes en ponerte en contacto con
                nosotros. Estamos aquí para ayudarte.
              </p>

              <div className="contact-details" style={{ marginBottom: '30px' }}>
                <div
                  className="contact-item"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '20px',
                    gap: '15px',
                  }}
                >
                  <span
                    className="material-icons"
                    style={{ color: 'var(--primary-color)', fontSize: '24px' }}
                  >
                    location_on
                  </span>
                  <div>
                    <h3
                      style={{
                        fontSize: '1.2rem',
                        marginBottom: '5px',
                        color: 'var(--primary-dark)',
                      }}
                    >
                      Dirección
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                      {contactInfo?.address ? (
                        <>
                          {contactInfo.address.street}
                          <br />
                          {contactInfo.address.postalCode} {contactInfo.address.city}, {contactInfo.address.country}
                        </>
                      ) : (
                        <>
                          Calle Principal 123
                          <br />
                          28001 Madrid, España
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div
                  className="contact-item"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '20px',
                    gap: '15px',
                  }}
                >
                  <span
                    className="material-icons"
                    style={{ color: 'var(--primary-color)', fontSize: '24px' }}
                  >
                    phone
                  </span>
                  <div>
                    <h3
                      style={{
                        fontSize: '1.2rem',
                        marginBottom: '5px',
                        color: 'var(--primary-dark)',
                      }}
                    >
                      Teléfono
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                      {contactInfo?.phone || '+34 91 123 45 67'}
                    </p>
                  </div>
                </div>
                <div
                  className="contact-item"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '20px',
                    gap: '15px',
                  }}
                >
                  <span
                    className="material-icons"
                    style={{ color: 'var(--primary-color)', fontSize: '24px' }}
                  >
                    email
                  </span>
                  <div>
                    <h3
                      style={{
                        fontSize: '1.2rem',
                        marginBottom: '5px',
                        color: 'var(--primary-dark)',
                      }}
                    >
                      Email
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                      {contactInfo?.email || 'info@prokidsapp.com'}
                    </p>
                  </div>
                </div>

              </div>

              <div className="social-contact">
                <h3
                  style={{ fontSize: '1.2rem', marginBottom: '15px', color: 'var(--primary-dark)' }}
                >
                  Síguenos en Redes Sociales
                </h3>
                <div className="social-icons" style={{ display: 'flex', gap: '15px' }}>
                  {socialMedia && socialMedia.length > 0 ? (
                    socialMedia.map((social, index) => (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon"
                        title={`Síguenos en ${social.platform}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--primary-light)',
                          color: 'var(--primary-color)',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <span className="material-icons">{social.icon}</span>
                      </a>
                    ))
                  ) : (
                    // Redes sociales por defecto si no hay configuración
                    <>
                      <a
                        href="https://www.facebook.com/prokids.experience"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--primary-light)',
                          color: 'var(--primary-color)',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <span className="material-icons">facebook</span>
                      </a>
                      <a
                        href="https://www.instagram.com/prokids.experience?igsh=MXFwOGx2azU3bWwxYg=="
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--primary-light)',
                          color: 'var(--primary-color)',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <span className="material-icons">photo_camera</span>
                      </a>
                      <a
                        href="https://www.youtube.com/@ProKidsExperience"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--primary-light)',
                          color: 'var(--primary-color)',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <span className="material-icons">smart_display</span>
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div
              className="contact-form"
              style={{
                flex: '1',
                minWidth: '300px',
                backgroundColor: 'var(--background-grey)',
                padding: '30px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px var(--shadow-color)',
              }}
            >
              <h2 style={{ fontSize: '2rem', color: 'var(--primary-dark)', marginBottom: '20px' }}>
                Envíanos un Mensaje
              </h2>

              {formStatus.isSubmitted && (
                <div
                  className={`form-message ${formStatus.isError ? 'error' : 'success'}`}
                  style={{
                    padding: '15px',
                    marginBottom: '20px',
                    borderRadius: '4px',
                    backgroundColor: formStatus.isError
                      ? 'rgba(244, 67, 54, 0.1)'
                      : 'rgba(76, 175, 80, 0.1)',
                    color: formStatus.isError ? 'var(--error-color)' : 'var(--success-color)',
                    border: `1px solid ${formStatus.isError ? 'var(--error-color)' : 'var(--success-color)'}`,
                  }}
                >
                  {formStatus.message}
                </div>
              )}

              <MobileOptimizedForm
                onSubmit={handleSubmit}
                error={formStatus.isError ? formStatus.message : ''}
                success={formStatus.isSubmitted && !formStatus.isError ? formStatus.message : ''}
                style={{
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  padding: '0',
                }}
              >
                <MobileOptimizedInput
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  label="Nombre Completo"
                  placeholder="Tu nombre completo"
                  required
                  autoComplete="name"
                />
                <MobileOptimizedInput
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  label="Correo Electrónico"
                  placeholder="tu@email.com"
                  required
                  autoComplete="email"
                  inputMode="email"
                />
                <MobileOptimizedInput
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  label="Asunto"
                  placeholder="¿En qué podemos ayudarte?"
                  required
                />
                <MobileOptimizedTextarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  label="Mensaje"
                  placeholder="Escribe tu mensaje aquí..."
                  rows={5}
                  maxLength={1000}
                  showCharCount
                  required
                />
                <MobileOptimizedButton
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                  loading={formStatus.isSubmitting}
                  disabled={formStatus.isSubmitting}
                  icon="send"
                >
                  {formStatus.isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
                </MobileOptimizedButton>
              </MobileOptimizedForm>
            </div>
          </div>
          {/*
          <div className="contact-map" style={{ marginBottom: '60px' }}>
            <h2
              style={{
                fontSize: '2rem',
                color: 'var(--primary-dark)',
                marginBottom: '20px',
                textAlign: 'center',
              }}
            >
              Nuestra Ubicación
            </h2>
            <div
              className="map-container"
              style={{
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px var(--shadow-color)',
                height: '400px',
                position: 'relative',
              }}
            >
              {mapSettings ? (
                <iframe
                  src={generateMapIframeUrl(mapSettings.latitude, mapSettings.longitude, mapSettings.zoom)}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación de Liga Prokids"
                />
              ) : (
                // Fallback a imagen placeholder si no hay configuración de mapa
                <img
                  src="/img/map-placeholder.svg"
                  alt="Mapa de ubicación"
                  className="map-image"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              )}
            </div>
            {mapSettings && (
              <p style={{
                textAlign: 'center',
                marginTop: '10px',
                fontSize: '0.9rem',
                color: 'var(--text-secondary)'
              }}>
                📍 Haz clic en el mapa para abrir en Google Maps y obtener direcciones
              </p>
            )}
          </div>
            */}

        </div>
      </section>
    </>
  );
};

export default Contact;
